import { Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { Experience, ExperienceInput, SaveExperienceInput } from 'sr-types/lib/person/v1/graphql';
import { UserContext } from '../../../common/auth/UserContext';
import Confirm from '../../../common/components/Confirm';
import {
    createProfessionalExpriencesInputObject,
    useRemovePersonExperience,
    useUpdateProfessionalExperiences
} from '../../../common/components/person/person';
import I18n, { useI18n } from '../../../common/i18n/I18n';
import { PROFILES } from '../../../common/nav/apps';
import { successToastMessage } from '../../../common/utils/commonUtils';
import useHistory from '../../../common/utils/useHistory';
import { buildExperienceRows, WorkEntriesDatagrid } from '../../../common/workEntries';
import { EMPTY_PROFESSIONAL_EXPERIENCES } from '../../person';
import { experienceToInput } from '../detailsPageUtilities/personBasicInformation';
import ProfessionalExperiencesEditor from '../editComponents/ProfessionalExperiencesEditor';
import SideCard from '../sideCard/SideCard';

export interface ProfessionalExperiencesCardProps {
    experiences: Experience[];
    allowedEdit: boolean;
    fromMarketplace?: boolean;
}

export default function ProfessionalExperiencesCard({
    experiences,
    allowedEdit,
    fromMarketplace = false
}: ProfessionalExperiencesCardProps) {
    const { application, userProfile } = useContext(UserContext);
    const { routeParams } = useHistory();
    const { entityId } = routeParams;

    const { updatePersonExperience, isSaving } = useUpdateProfessionalExperiences();
    const { removePersonExperience, isRemoving } = useRemovePersonExperience();

    const [isExperiencesModalOpen, setIsExperiencesModalOpen] = useState<boolean>(false);
    const [isAddExperiencesModalOpen, setIsAddExperiencesModalOpen] = useState(false);
    const [createState, setCreateState] = useState<ExperienceInput>(EMPTY_PROFESSIONAL_EXPERIENCES);
    const [existingState, setExistingState] = useState<ExperienceInput>(undefined);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [experiencePendingDelete, setExperiencePendingDelete] = useState<Experience | null>(null);

    const isUserProfile = application.id === PROFILES.id;
    const keyExperienceTooltip = useI18n('person.experiences.key.experience');
    const keyExperienceQuickFilterTooltip = useI18n('experiences.filter.key.experience');
    const id = useMemo(
        () => (isUserProfile && !entityId ? userProfile.personReference.id : entityId),
        [entityId, isUserProfile, userProfile.personReference.id]
    );

    const onClose = () => {
        setIsAddExperiencesModalOpen(false);
        setCreateState(EMPTY_PROFESSIONAL_EXPERIENCES);
        setExistingState(undefined);
    };

    const updatePersonProfessionalExperience = () => {
        const experienceInput = createProfessionalExpriencesInputObject(createState);

        const input: SaveExperienceInput = {
            new: experienceInput,
            existing: existingState || {},
            forceUserChange: true
        };
        updatePersonExperience({ id, input }).then(() => {
            onClose();
            successToastMessage('person.professional.experience.form.add.success');
        });
    };

    const handleEditExperience = useCallback(
        (experience: Experience) => {
            if (!allowedEdit || !id) return;
            const input = experienceToInput(
                cloneDeep({
                    ...experience,
                    dateRange: experience.dateRange ?? { start: '', end: '' }
                })
            );
            setExistingState(cloneDeep(input));
            setCreateState(input);
            setIsAddExperiencesModalOpen(true);
        },
        [allowedEdit, id]
    );

    const handleRequestDeleteExperience = useCallback(
        (experience: Experience) => {
            if (!allowedEdit || !id) return;
            setExperiencePendingDelete(experience);
            setIsDeleteConfirmOpen(true);
        },
        [allowedEdit, id]
    );

    const handleConfirmDeleteExperience = useCallback(() => {
        if (!experiencePendingDelete || !id) return;
        removePersonExperience({ id, input: experienceToInput(experiencePendingDelete) }).finally(() => {
            setIsDeleteConfirmOpen(false);
            setExperiencePendingDelete(null);
        });
    }, [experiencePendingDelete, id, removePersonExperience]);

    const experiencesList = useMemo(() => {
        const items = experiences ?? [];
        if (items.length === 0) return null;
        const rowActionsOn = Boolean(allowedEdit && id);
        const inlineAddButton = allowedEdit && id ? { onAdd: () => setIsAddExperiencesModalOpen(true) } : undefined;
        const rows = buildExperienceRows(items);

        return (
            <WorkEntriesDatagrid
                rows={rows}
                enableSelection={false}
                enableKeyQuickFilter
                enableReorder={false}
                rowActionsEnabled={rowActionsOn}
                showBackButton={false}
                compactHorizontalPadding={fromMarketplace}
                inlineAddButton={inlineAddButton}
                keyEntryTooltip={keyExperienceTooltip}
                keyQuickFilterTooltip={keyExperienceQuickFilterTooltip}
                onEdit={(row) => handleEditExperience(row.source)}
                onRequestDelete={(row) => handleRequestDeleteExperience(row.source)}
            />
        );
    }, [
        experiences,
        allowedEdit,
        id,
        fromMarketplace,
        keyExperienceTooltip,
        keyExperienceQuickFilterTooltip,
        handleEditExperience,
        handleRequestDeleteExperience
    ]);

    return (
        <>
            <SideCard
                id={'professional-experiences'}
                title={<I18n token="person.professional.experiences.title" />}
                isModalOpen={isExperiencesModalOpen}
                allowedEdit={allowedEdit}
                modalTitle={<I18n token="modal.professional.experiences.title" />}
                setIsModalOpen={setIsExperiencesModalOpen}
                setIsCreateModalOpen={setIsAddExperiencesModalOpen}
                fromMarketplace={fromMarketplace}
                fullWidthContent
                suppressAddButton={Boolean(allowedEdit && id)}
            >
                {experiencesList ? [experiencesList] : []}
            </SideCard>
            {isAddExperiencesModalOpen && (
                <ProfessionalExperiencesEditor
                    isAddExperiencesModalOpen={isAddExperiencesModalOpen}
                    createState={createState}
                    existingState={existingState}
                    setCreateState={setCreateState}
                    onSave={updatePersonProfessionalExperience}
                    onClose={onClose}
                    isSaving={isSaving}
                />
            )}
            <Confirm
                open={isDeleteConfirmOpen}
                title={<I18n token="person.form.confirm.delete.title" />}
                onConfirm={handleConfirmDeleteExperience}
                body={
                    <Typography>
                        <I18n token="person.form.confirm.delete.message" />
                    </Typography>
                }
                onCancel={() => {
                    setIsDeleteConfirmOpen(false);
                    setExperiencePendingDelete(null);
                }}
                isLoading={isRemoving}
            />
        </>
    );
}
